import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { toast } from "react-hot-toast";
import { 
  UserGroupIcon,
  BuildingOffice2Icon,
  ArrowLeftIcon
} from "@heroicons/react/24/outline";
import DashboardHeader from "../../../../../components/dashboard/dashboardHeader";
import { ResourceLoading } from "../../../../../components/utils/ResourceLoading";
import { ResourceError } from "../../../../../components/utils/ResourceError";
import RoleOnly, { useUserHasAccess } from "../../../../../routes/roleOnly";
import { getRole, createRole, updateRole } from "../../../../../api/hse/competency";
import { fetchDepartments } from "../../../../../api/department";

/**
 * HSE/KS Competency Role Edit - Create or edit role
 * ISO 9001:2015 Clause 7.2 - Competence
 */
export const HseKsCompetencyRoleEdit = () => {
  const { t } = useTranslation();
  const { id } = useParams();
  const navigate = useNavigate();
  const isNew = !id;
  
  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(false);
  const [departments, setDepartments] = useState([]);
  const [formData, setFormData] = useState({
    name: "",
    description: "",
    department_id: null,
  });

  const canManage = useUserHasAccess({ role: "hse_ks_competency_manage", action: isNew ? "create" : "edit" });

  useEffect(() => {
    fetchDepartmentsList();
    if (!isNew) {
      fetchRole();
    }
  }, [id]);

  const fetchDepartmentsList = async () => {
    try {
      const response = await fetchDepartments({});
      // Ensure we always have an array
      let departmentsList = [];
      if (Array.isArray(response)) {
        departmentsList = response;
      } else if (response?.departments && Array.isArray(response.departments)) {
        departmentsList = response.departments;
      } else if (response?.data && Array.isArray(response.data)) {
        departmentsList = response.data;
      }
      setDepartments(departmentsList);
    } catch (err) {
      console.error("Error fetching departments:", err);
      setDepartments([]); // Set to empty array on error
    }
  };

  const fetchRole = async () => {
    try {
      setLoading(true);
      setError(false);
      
      const response = await getRole(id);
      const roleData = response.role || response;
      setFormData({
        name: roleData.name || "",
        description: roleData.description || "",
        department_id: roleData.department_id || null,
      });
    } catch (err) {
      console.error("Error fetching role:", err);
      setError(true);
      toast.error(t("Kunne ikke laste rolle"));
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value === "" ? null : (name === "department_id" ? parseInt(value) : value),
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name.trim()) {
      toast.error(t("Navn er påkrevd"));
      return;
    }

    try {
      setSaving(true);
      
      let response;
      if (isNew) {
        response = await createRole(formData);
        toast.success(t("Rolle opprettet"));
        navigate(`/dashboard/hse-ks/competency/matrix/${response.role?.id || response.id}`);
      } else {
        response = await updateRole(id, formData);
        toast.success(t("Rolle oppdatert"));
        navigate(`/dashboard/hse-ks/competency/matrix/${id}`);
      }
    } catch (err) {
      console.error("Error saving role:", err);
      const errorMessage = err.response?.data?.error || err.message || t("Kunne ikke lagre rolle");
      toast.error(errorMessage);
    } finally {
      setSaving(false);
    }
  };

  if (!canManage) {
    navigate("/dashboard/hse-ks/competency/matrix");
    return null;
  }

  if (loading) {
    return <ResourceLoading size="base" />;
  }

  if (error) {
    return <ResourceError errors={[t("Kunne ikke laste rolle")]} />;
  }

  return (
    <DashboardHeader
      title={isNew ? t("Ny rolle") : t("Rediger rolle")}
      subtitle={t("Rollekjema")}
      backButton={{
        label: isNew ? t("Tilbake til roller") : t("Tilbake til roller"),
        action: () => navigate("/dashboard/hse-ks/competency/roles"),
        icon: ArrowLeftIcon,
      }}
    >
      <div className="mx-auto mt-8 max-w-4xl sm:px-6 lg:px-8">
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Basic Information */}
          <div className="bg-white shadow sm:rounded-lg">
            <div className="px-4 py-5 sm:p-6">
              <h3 className="text-lg font-medium text-gray-900 mb-4">{t("Grunnleggende informasjon")}</h3>
              
              <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
                {/* Name */}
                <div className="sm:col-span-2">
                  <label htmlFor="name" className="block text-sm font-medium text-gray-700">
                    {t("Navn")} <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    name="name"
                    id="name"
                    required
                    value={formData.name}
                    onChange={handleChange}
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                    placeholder={t("F.eks. Prosjektleder, Montør, etc.")}
                  />
                </div>

                {/* Department */}
                <div>
                  <label htmlFor="department_id" className="block text-sm font-medium text-gray-700">
                    {t("Avdeling")}
                  </label>
                  <select
                    name="department_id"
                    id="department_id"
                    value={formData.department_id || ""}
                    onChange={handleChange}
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                  >
                    <option value="">{t("Ingen avdeling")}</option>
                    {departments.map((dept) => (
                      <option key={dept.id} value={dept.id}>
                        {dept.name}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Description */}
                <div className="sm:col-span-2">
                  <label htmlFor="description" className="block text-sm font-medium text-gray-700">
                    {t("Beskrivelse")}
                  </label>
                  <textarea
                    name="description"
                    id="description"
                    rows={4}
                    value={formData.description}
                    onChange={handleChange}
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                    placeholder={t("Beskriv rollen...")}
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Actions */}
          <div className="flex justify-end space-x-3">
            <button
              type="button"
              onClick={() => navigate("/dashboard/hse-ks/competency/matrix")}
              className="px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
            >
              {t("Avbryt")}
            </button>
            <button
              type="submit"
              disabled={saving}
              className="px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50"
            >
              {saving ? t("Lagrer...") : isNew ? t("Opprett") : t("Lagre endringer")}
            </button>
          </div>
        </form>
      </div>
    </DashboardHeader>
  );
};

